import {
	ADD_CAPTURE_IMAGE,
	COMPLETE_CAPTURE_IMAGE_UPLOAD,
	REQUEST_CAPTURE_IMAGE_METADATA,
	SET_CAPTURE_ANNOTATIONS,
	UPLOAD_CAPTURE_IMAGE,
	SET_CAPTURE_ANNOTATION_BOX,
	SET_CAPTURE_ERROR
} from '../../actions/actiontypes';

import {v4 as uuidv4} from 'uuid';
import initialstate from '../initialstate';

/**
 * This function creates immutable state for an imageset item
 * from changed values, or adds a new one if none exists
 * for a given body Id
 */
function merge(state, bodyId, captureId, valueObject) {
	// Default filter function -> Filter by id (captureId)
	const screenshotFilter = (item) => item.id === captureId;

	return mergeByFilter(state, bodyId, screenshotFilter, valueObject);
}

function mergeByFilter(state, bodyId, screenshotFilter, valueObject) {
	let imageset;

	if (bodyId in state.imagesets) {
		imageset = [...state.imagesets[bodyId]];
		let index = imageset.findIndex((item) => screenshotFilter(item));

		if (index === -1) {
			imageset.push(valueObject);
		} else {
			imageset[index] = {
				...imageset[index],
				...valueObject
			};
		}
	} else {
		imageset = [valueObject];
	}

	return {
		...state,
		imagesets: {
			...state.imagesets,
			[bodyId]: imageset
		}
	};
}

export function getScreenshotIndex(screenshotList, documentId) {
	const screenshotIndex = screenshotList.findIndex((item) => item.documentId === documentId);

	return screenshotIndex;
}

export function getScreenshotDetails(storeState, bodyId, documentId) {
	let screenshotData;

	if (storeState && storeState.imagesets && storeState.imagesets[bodyId]) {
		const screenshotIndex = getScreenshotIndex(storeState.imagesets[bodyId], documentId);
		if (screenshotIndex > -1) {
			screenshotData = storeState.imagesets[bodyId][screenshotIndex];
		}
	}

	return screenshotData;
}

export default function captureAnnotationReducer(state = initialstate.captureAnnotation, action) {
	let bodyId;
	let documentId;
	let screenshotIndex;
	let newState;

	switch (action.type) {
		case ADD_CAPTURE_IMAGE: {
			const {base64Image, blobImage, hash, versionNumber, document, documentName, annotations} = action.payload;
			bodyId = action.payload.bodyId;

			return merge(state, bodyId, null, {
				id: Date.now(),
				annotations: annotations,
				base64Image,
				blobImage,
				hash,
				documentName: documentName ? documentName : document?.documentName,
				documentId: document ? document.id : null,
				document,
				transactionId: uuidv4(),
				versionNumber,
				needsMetadata: document ? false : true,
				needsUpload: document ? true : false
			});
		}
		case SET_CAPTURE_ANNOTATIONS: {
			let {captureId, annotations} = action.payload;
			bodyId = action.payload.bodyId;

			return merge(state, bodyId, captureId, {
				annotations
			});
		}
		case REQUEST_CAPTURE_IMAGE_METADATA: {
			let {captureId} = action.payload;
			bodyId = action.payload.bodyId;

			return merge(state, bodyId, captureId, {
				documentId: action.response.id,
				document: action.response,
				needsMetadata: false,
				needsUpload: true
			});
		}
		case UPLOAD_CAPTURE_IMAGE: {
			let {captureId} = action.payload;
			bodyId = action.payload.bodyId;

			return merge(state, bodyId, captureId, {
				needsUpload: false,
				needsUploadCompletion: true
			});
		}
		case COMPLETE_CAPTURE_IMAGE_UPLOAD: {
			let {captureId} = action.payload;
			bodyId = action.payload.bodyId;
			let imageset;
			if (bodyId in state.imagesets) {
				imageset = [...state.imagesets[bodyId]];
				const screenshotFilter = (item) => item.id === captureId;
				let index = imageset.findIndex((item) => screenshotFilter(item));

				return merge(state, bodyId, captureId, {
					document: {...imageset[index].document, ...action.response},
					needsUploadCompletion: false,
					uploaded: true
				});
			}
			return merge(state, bodyId, captureId, {
				document: {...state.document, ...action.response},
				needsUploadCompletion: false,
				uploaded: true
			});
		}
		case SET_CAPTURE_ERROR: {
			const {
				bodyId,
				captureId,
				documentName,
				engagementId,
				transactionId,
				errorActionType,
				errorException
			} = action.payload;

			return merge(state, bodyId, captureId, {
				documentName,
				engagementId,
				transactionId,
				error: {actionType: errorActionType, exception: errorException}
			});
		}
		case SET_CAPTURE_ANNOTATION_BOX: {
			const screenshotData = getScreenshotDetails(state, action.bodyId, action.documentId);
			if (screenshotData) {
				// Update annotations collection with new geometry data. If annotationId == -1, add it to the collection (temporary bounding box)

				let screenshotAnnotations = screenshotData.annotations;
				const annotationIndex = screenshotAnnotations.findIndex((a) => a.id === action.annotationId);
				if (annotationIndex > -1) {
					screenshotAnnotations[annotationIndex] = {
						...screenshotAnnotations[annotationIndex],
						...action.boundingBoxData
					};
				} else if (action.annotationId === -1) {
					screenshotAnnotations = screenshotAnnotations.concat({
						id: action.annotationId,
						...action.boundingBoxData
					});
				}

				// Save geometry data to newAnnotation property
				const newScreenshotData = {
					annotations: screenshotAnnotations,
					newAnnotationData: {
						...screenshotData.newAnnotationData,
						...action.boundingBoxData
					}
				};

				// Merge new state data:
				const screenshotFilter = (img) => img.documentId === action.documentId;

				newState = mergeByFilter(state, action.bodyId, screenshotFilter, newScreenshotData);
				return newState;
			}

			return state;
		}
		default:
			return state;
	}
}
